import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { WebSocketService } from '@vinlivt/websocket';
import { Observable, Subject, catchError, map, of, switchMap, take, tap } from 'rxjs';

import { environment } from '../../../../environments/environment';

import { AccountAdvisorService } from '../account-advisor-service/account-advisor.service';

import {
  AdvisorDetailsModel,
  FollowUpCounts,
  FollowUpDetail,
  FollowUpListFilter,
  FollowUpListItem,
  FollowUpPage,
  FollowUpSnoozeOption,
  FollowUpWriteRequest,
  normalizeFollowUpCategory,
} from '../../model';

import RequestCache from '../../utils/RequestCache';

/** Payload: the advisor's current counts (same shape as GET /followup/follow-ups/counts) */
const FOLLOW_UP_COUNTS_CHANGED_TOPIC = 'followup/counts-changed';

/** Shown when the counts request fails – the next change or websocket event loads them again */
const EMPTY_COUNTS: FollowUpCounts = { overdue: 0, today: 0, planned: 0, doneThisWeek: 0, doneTotal: 0, due: 0 };

@Injectable({ providedIn: 'root' })
export class FollowUpService {
  /** Counts of the logged-in advisor – used for the global badges (header, sidebar, drawer) */
  public readonly globalStatistics: RequestCache<FollowUpCounts>;

  /**
   * Emits after every successful create, update, delete, done, reopen and snooze – from any place in the app.
   * Lists that are not the source of the change (client file card and tab, drawer, …) reload on it.
   */
  public readonly changed$ = new Subject<void>();

  constructor(
    private http: HttpClient,
    private accountAdvisorService: AccountAdvisorService,
    private webSocketService: WebSocketService,
  ) {
    this.globalStatistics = new RequestCache(
      this.accountAdvisorService.advisorDetails.observable.pipe(
        take(1),
        switchMap((advisor: AdvisorDetailsModel) => this.getFollowUpStatistics(advisor.userId)),
        // An error would end the badge subscriptions (sidebar, header, drawer) until the page is reloaded
        catchError(() => of(EMPTY_COUNTS)),
      ),
    );

    // Live updates – follow-ups become due over time or are changed by a colleague / in another tab.
    // The payload already contains the new counts, so the badges (sidebar, header, drawer) update without
    // a request; the lists (overview, drawer, client file) reload through changed$.
    this.webSocketService
      .subscribe<FollowUpCounts>(FOLLOW_UP_COUNTS_CHANGED_TOPIC)
      .subscribe((counts: FollowUpCounts): void => {
        if (counts) {
          this.globalStatistics.setValue(counts);
        } else {
          this.globalStatistics.clearCache();
        }

        this.changed$.next();
      });
  }

  public getAllFollowUps(filter: FollowUpListFilter = {}): Observable<FollowUpPage> {
    return this.http
      .get<FollowUpPage>(`${environment.apiEndpoint}/followup/follow-ups`, {
        params: this.toParams(filter),
      })
      .pipe(map((page: FollowUpPage): FollowUpPage => this.normalizePage(page)));
  }

  public getFollowUpsByClient(
    clientId: string,
    filter: Omit<FollowUpListFilter, 'clientId'> = {},
  ): Observable<FollowUpPage> {
    return this.http
      .get<FollowUpPage>(`${environment.apiEndpoint}/followup/clients/${clientId}/follow-ups`, {
        params: this.toParams(filter),
      })
      .pipe(map((page: FollowUpPage): FollowUpPage => this.normalizePage(page)));
  }

  public getFollowUpStatistics(assigneeId?: string | null): Observable<FollowUpCounts> {
    return this.http.get<FollowUpCounts>(`${environment.apiEndpoint}/followup/follow-ups/counts`, {
      params: this.toParams({ assigneeId }),
    });
  }

  public getFollowUp(id: string): Observable<FollowUpDetail> {
    return this.http
      .get<FollowUpDetail>(`${environment.apiEndpoint}/followup/follow-ups/${id}`)
      .pipe(map((followUp: FollowUpDetail): FollowUpDetail => this.normalizeDetail(followUp)));
  }

  public createFollowUp(request: FollowUpWriteRequest): Observable<FollowUpDetail> {
    return this.http.post<FollowUpDetail>(`${environment.apiEndpoint}/followup/follow-ups`, request).pipe(
      map((followUp: FollowUpDetail): FollowUpDetail => this.normalizeDetail(followUp)),
      tap((): void => this.notifyChanged()),
    );
  }

  public updateFollowUp(id: string, request: FollowUpWriteRequest): Observable<FollowUpDetail> {
    return this.http.put<FollowUpDetail>(`${environment.apiEndpoint}/followup/follow-ups/${id}`, request).pipe(
      map((followUp: FollowUpDetail): FollowUpDetail => this.normalizeDetail(followUp)),
      tap((): void => this.notifyChanged()),
    );
  }

  public deleteFollowUp(id: string): Observable<void> {
    return this.http
      .delete<void>(`${environment.apiEndpoint}/followup/follow-ups/${id}`)
      .pipe(tap((): void => this.notifyChanged()));
  }

  public markFollowUpAsDone(id: string): Observable<FollowUpDetail> {
    return this.http.post<FollowUpDetail>(`${environment.apiEndpoint}/followup/follow-ups/${id}/done`, {}).pipe(
      map((followUp: FollowUpDetail): FollowUpDetail => this.normalizeDetail(followUp)),
      tap((): void => this.notifyChanged()),
    );
  }

  public reopenFollowUp(id: string): Observable<FollowUpDetail> {
    return this.http.post<FollowUpDetail>(`${environment.apiEndpoint}/followup/follow-ups/${id}/reopen`, {}).pipe(
      map((followUp: FollowUpDetail): FollowUpDetail => this.normalizeDetail(followUp)),
      tap((): void => this.notifyChanged()),
    );
  }

  public snoozeFollowUp(id: string, by: FollowUpSnoozeOption): Observable<FollowUpDetail> {
    return this.http.post<FollowUpDetail>(`${environment.apiEndpoint}/followup/follow-ups/${id}/snooze`, { by }).pipe(
      map((followUp: FollowUpDetail): FollowUpDetail => this.normalizeDetail(followUp)),
      tap((): void => this.notifyChanged()),
    );
  }

  /** Automatic follow-ups can come with categories like "contract expiry" – map them to our keys */
  private normalizePage(page: FollowUpPage): FollowUpPage {
    return {
      ...page,
      content: (page?.content ?? []).map((item: FollowUpListItem) => ({
        ...item,
        category: normalizeFollowUpCategory(item.category),
      })),
    };
  }

  private normalizeDetail(followUp: FollowUpDetail): FollowUpDetail {
    return followUp ? { ...followUp, category: normalizeFollowUpCategory(followUp.category) } : followUp;
  }

  private notifyChanged(): void {
    this.globalStatistics.clearCache();
    this.changed$.next();
  }

  private toParams(filter: FollowUpListFilter): HttpParams {
    let params = new HttpParams();

    const values: Record<string, string | number | null | undefined> = {
      view: filter.view,
      assigneeId: filter.assigneeId,
      category: filter.category,
      clientId: filter.clientId,
      q: filter.search?.trim(),
      page: filter.page,
      size: filter.size,
    };

    for (const [key, value] of Object.entries(values)) {
      if (value !== undefined && value !== null && value !== '') {
        params = params.set(key, value);
      }
    }

    return params;
  }
}
